import React, { type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

export type SwitcherExperimentCohort = 'control' | 'test' | 'not-enrolled' | undefined;

const overflowStorageKey = '@atlassian/switcher-overflow-experiment-cohort';

interface useSwitcherOverflowExperimentResult {
	cohort: SwitcherExperimentCohort;
	setOverflowCohort: (cohort: SwitcherExperimentCohort) => void;
}

interface useSwitcherThirdPartyExperimentResult {
	cohort: SwitcherExperimentCohort;
	setThirdPartyCohort: (cohort: SwitcherExperimentCohort) => void;
}

type SwitcherExperimentContextProvider = {
	setSwitcherOverflowExperimentCohort: (cohort: SwitcherExperimentCohort) => void;
	setSwitcherThirdPartyExperimentCohort: (cohort: SwitcherExperimentCohort) => void;
	switcherOverflowExperimentCohort: SwitcherExperimentCohort;
	switcherThirdPartyExperimentCohort: SwitcherExperimentCohort;
};

export const ExperimentContext = React.createContext<SwitcherExperimentContextProvider | undefined>(
	undefined,
);

export const SwitcherExperimentProvider = ({ children }: { children: ReactNode }) => {
	const [switcherOverflowExperimentCohort, setSwitcherOverflowExperimentCohort] =
		useState<SwitcherExperimentCohort>();

	const [switcherThirdPartyExperimentCohort, setSwitcherThirdPartyExperimentCohort] =
		useState<SwitcherExperimentCohort>();

	const contextValue = useMemo<SwitcherExperimentContextProvider>(
		() => ({
			switcherOverflowExperimentCohort,
			setSwitcherOverflowExperimentCohort,
			switcherThirdPartyExperimentCohort,
			setSwitcherThirdPartyExperimentCohort,
		}),
		[
			switcherOverflowExperimentCohort,
			setSwitcherOverflowExperimentCohort,
			switcherThirdPartyExperimentCohort,
			setSwitcherThirdPartyExperimentCohort,
		],
	);

	useEffect(() => {
		if (window?.sessionStorage && switcherOverflowExperimentCohort) {
			window.sessionStorage.setItem(overflowStorageKey, switcherOverflowExperimentCohort);
		}
	}, [switcherOverflowExperimentCohort]);

	return <ExperimentContext.Provider value={contextValue}>{children}</ExperimentContext.Provider>;
};

export const useExperimentProvider = () => useContext(ExperimentContext);

export const useSwitcherOverflowExperiment = (): useSwitcherOverflowExperimentResult => {
	const experimentContext = useExperimentProvider();

	return {
		cohort: experimentContext?.switcherOverflowExperimentCohort,
		setOverflowCohort: experimentContext
			? experimentContext.setSwitcherOverflowExperimentCohort
			: () => {},
	};
};

export const useIsSwitcherOverflowTestCohort = () => {
	const { cohort } = useSwitcherOverflowExperiment();
	if (cohort) {
		return cohort === 'test';
	}

	// fallback to session storage
	return window?.sessionStorage?.getItem(overflowStorageKey) === 'test';
};

export const useSwitcherThirdPartyExperiment = (): useSwitcherThirdPartyExperimentResult => {
	const experimentContext = useExperimentProvider();

	return {
		cohort: experimentContext?.switcherThirdPartyExperimentCohort,
		setThirdPartyCohort: experimentContext
			? experimentContext.setSwitcherThirdPartyExperimentCohort
			: () => {},
	};
};

export const useIsSwitcherThirdPartyTestCohort = () => {
	const { cohort } = useSwitcherThirdPartyExperiment();
	return cohort === 'test';
};
